/**
 * Projection for `WbsTree` — the platform as a numbered work-breakdown
 * structure: every section, every page, every sub-page, one outline.
 *
 * Reviewer ask (Jay, v1.8 feedback): "you can still deep dive through several
 * sub-sections (Workflows > 07 Parts & Supply > Stock & Replenishment) … is
 * there an overarching mind-map or WBS where we can see all these connected
 * sections?" This is that map, built from the same data the left rail is, so
 * the two cannot drift:
 *
 * - **Sections and pages** come from `OEM_SECTIONS` in `shared/oem-nav.ts`.
 *   A nav row flagged `sub` nests under the row above it, exactly as the rail
 *   indents it — which is what makes the ten workflow modules own their steps,
 *   and the three asset divisions own their sub-classes.
 * - **Workflow steps** are cross-checked against the step registry
 *   (`WORKFLOW_STEPS` in `shared/seed/workflow-steps.ts`): a step the registry
 *   assigns to a module but the rail does not list is appended under that
 *   module, so the tree always shows every step `ModuleScene` can open.
 *
 * Codes are positional: section · row · sub-row. Workflows is section 3 and
 * its ten modules are its first ten rows, so `3.07` is 07 Parts & Supply and
 * `3.07.2` is Stock & replenishment — the module number reads straight
 * through.
 *
 * `deepDive` marks what an executive does not meet in the rail: a row above
 * `TIER_MAX`, or one outside `EXECUTIVE_NAV_IDS`. Every node is still routed —
 * deep dive means "behind a drill-down", not "unavailable".
 *
 * Pure: no React, no fetching, no engine. Transformation layer (layer 3).
 *
 * @see src/versions/v1/modules/help/WbsTree.tsx
 * @see docs/logic/help/structure.md
 */

import type { LucideIcon } from 'lucide-react';
import {
  isExecutiveNavId,
  OEM_SECTIONS,
  TIER_MAX,
  type NavTier,
  type OemNavItem,
  type OemNavSection,
  type OemSectionId,
} from '../../shared/oem-nav';
import { NAVMAP } from '../../shared/seed/roles';
import { WORKFLOW_STEPS } from '../../shared/seed/workflow-steps';

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

/** What a node is — drives nothing but the row's type hint and tests. */
export type WbsKind =
  | 'section'
  | 'page'
  | 'module'
  | 'step'
  | 'loop'
  | 'division'
  | 'subclass'
  | 'site';

/** One node of the outline. */
export interface WbsNode {
  /** Stable id: the nav row id, `section-<id>`, or `step-<id>` for a registry-only step. */
  readonly id: string;
  /** Positional WBS code — `3`, `3.07`, `3.07.2`. */
  readonly code: string;
  readonly label: string;
  /** Second line: a section's purpose or a step's tag. */
  readonly hint?: string;
  /** Absolute route. Every node has one. */
  readonly to: string;
  /** Nav tier. A section carries the lowest tier among its rows. */
  readonly tier: NavTier;
  readonly kind: WbsKind;
  /** The rail's own icon for the row, so the outline and the rail match. */
  readonly icon: LucideIcon;
  /** Not in the executive rail (tier above `TIER_MAX`, or not an executive row). */
  readonly deepDive: boolean;
  readonly children: readonly WbsNode[];
}

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

/** Section landing routes. `home` points at the Home tiles page, not Asset360 (which is 1.01). */
const SECTION_LANDING: Readonly<Record<OemSectionId, string>> = {
  home: '/home',
  issues: '/issues',
  workflows: '/workflows',
  loops: '/loops',
  assets: '/assets',
  sites: '/sites',
  integration: '/integration',
  help: '/help',
};

/** Section purpose lines, straight off `NAVMAP` (the Help map's own copy). */
const SECTION_HINT: Readonly<Record<string, string>> = {
  ...Object.fromEntries(NAVMAP.map((s) => [s.key, s.what])),
  help: 'How the platform fits together, how to move around it, and who uses what.',
};

/** Routes an executive can reach from the rail — a section landing counts when a row points at it. */
const EXECUTIVE_ROUTES: ReadonlySet<string> = new Set(
  OEM_SECTIONS.flatMap((s) => s.items)
    .filter((item) => isExecutiveNavId(item.id))
    .map((item) => item.to),
);

const pad2 = (n: number): string => String(n).padStart(2, '0');

function kindOf(section: OemSectionId, item: OemNavItem): WbsKind {
  if (item.id.startsWith('module-')) return 'module';
  if (section === 'workflows' && item.sub) return 'step';
  if (section === 'loops') return 'loop';
  if (item.id.startsWith('division-')) return 'division';
  if (item.id.startsWith('subclass-')) return 'subclass';
  if (item.id.startsWith('site-')) return 'site';
  return 'page';
}

/** `07 Parts & Supply` → `Parts & Supply`: the WBS code already carries the number. */
const moduleLabel = (label: string): string => label.replace(/^\d{2}\s+/, '');

/** `/workflows/steps/stock` → `stock`. */
const stepIdOf = (to: string): string | null => to.match(/^\/workflows\/steps\/([^/?]+)$/)?.[1] ?? null;

const STEP_TAG: Readonly<Record<string, string>> = Object.fromEntries(
  WORKFLOW_STEPS.map((s) => [s.id, s.tag]),
);

function isDeepDive(item: Pick<OemNavItem, 'id' | 'tier'>): boolean {
  return item.tier > TIER_MAX || !isExecutiveNavId(item.id);
}

/** Group a section's flat rows into parent rows with their `sub` children. */
function groupRows(section: OemNavSection): { row: OemNavItem; subs: OemNavItem[] }[] {
  const groups: { row: OemNavItem; subs: OemNavItem[] }[] = [];
  for (const item of section.items) {
    // The two executive-only rows duplicate a section landing; the section
    // node already is that landing.
    if (item.executiveOnly) continue;
    const last = groups[groups.length - 1];
    if (item.sub && last) last.subs.push(item);
    else groups.push({ row: item, subs: [] });
  }
  return groups;
}

/**
 * Steps the registry assigns to a module that the rail does not list, as
 * nav-shaped rows so they number and render like the rest.
 */
function registryOnlySteps(moduleId: string, listed: readonly OemNavItem[], icon: LucideIcon): OemNavItem[] {
  const have = new Set(listed.map((s) => stepIdOf(s.to)).filter(Boolean));
  return WORKFLOW_STEPS.filter((s) => s.mod === moduleId && !have.has(s.id)).map((s) => ({
    id: `step-${s.id}`,
    label: s.name,
    to: `/workflows/steps/${s.id}`,
    icon,
    tier: 2 as NavTier,
    sub: true,
  }));
}

/**
 * The whole platform as a numbered outline — one root per rail section, in
 * rail order.
 *
 * Invariants: every node has a route; codes are unique; a child is deep dive
 * whenever its parent is; a section is deep dive only when none of its rows
 * and not its own landing is in the executive rail.
 */
export function wbsTree(): WbsNode[] {
  return OEM_SECTIONS.map((section, si) => {
    const sectionCode = String(si + 1);

    const children: WbsNode[] = groupRows(section).map(({ row, subs }, ri) => {
      const code = `${sectionCode}.${pad2(ri + 1)}`;
      const kind = kindOf(section.id, row);
      const rowDeep = isDeepDive(row);
      const moduleId = kind === 'module' ? row.to.split('/').pop() ?? '' : '';
      const allSubs = kind === 'module' ? [...subs, ...registryOnlySteps(moduleId, subs, row.icon)] : subs;

      return {
        id: row.id,
        code,
        label: kind === 'module' ? moduleLabel(row.label) : row.label,
        to: row.to,
        tier: row.tier,
        kind,
        icon: row.icon,
        deepDive: rowDeep,
        children: allSubs.map((sub, i) => {
          const stepId = stepIdOf(sub.to);
          return {
            id: sub.id,
            code: `${code}.${i + 1}`,
            label: sub.label,
            hint: stepId ? STEP_TAG[stepId] : undefined,
            to: sub.to,
            tier: sub.tier,
            kind: kindOf(section.id, sub),
            icon: sub.icon,
            deepDive: rowDeep || isDeepDive(sub),
            children: [],
          };
        }),
      };
    });

    const landing = SECTION_LANDING[section.id];
    const tier = children.reduce<NavTier>((min, c) => (c.tier < min ? c.tier : min), 3);

    return {
      id: `section-${section.id}`,
      code: sectionCode,
      label: section.label,
      hint: SECTION_HINT[section.id],
      to: landing,
      tier,
      kind: 'section',
      icon: section.icon,
      deepDive: !EXECUTIVE_ROUTES.has(landing) && children.every((c) => c.deepDive),
      children,
    };
  });
}

// ---------------------------------------------------------------------------
// Reading the tree
// ---------------------------------------------------------------------------

/** Every node below this one, at any depth. */
export function descendantCount(node: WbsNode): number {
  return node.children.reduce((n, c) => n + 1 + descendantCount(c), 0);
}

/** Ids of every node that has children — the expand-all set. */
export function branchIds(nodes: readonly WbsNode[]): string[] {
  return nodes.flatMap((n) => (n.children.length ? [n.id, ...branchIds(n.children)] : []));
}

/** The three figures over the outline. */
export function wbsStats(nodes: readonly WbsNode[]): { eyebrow: string; value: string; detail: string }[] {
  const flat = flatten(nodes);
  const pages = flat.filter((n) => n.kind !== 'section');
  const deep = pages.filter((n) => n.deepDive).length;
  const depth = Math.max(...flat.map((n) => n.code.split('.').length));
  return [
    { eyebrow: 'Sections', value: String(nodes.length), detail: 'the headings in the rail' },
    { eyebrow: 'Pages', value: String(pages.length), detail: `over ${depth} levels` },
    { eyebrow: 'On the executive rail', value: String(pages.length - deep), detail: 'the lean path' },
    { eyebrow: 'Deep dive', value: String(deep), detail: 'one drill-down away' },
  ];
}

function flatten(nodes: readonly WbsNode[]): WbsNode[] {
  return nodes.flatMap((n) => [n, ...flatten(n.children)]);
}

// ---------------------------------------------------------------------------
// Filter
// ---------------------------------------------------------------------------

/** The outline narrowed to a query, plus what the UI needs to show it open. */
export interface WbsFilterResult {
  readonly nodes: readonly WbsNode[];
  /** Ids whose own label, code or hint matched — the rows to highlight. */
  readonly matchIds: ReadonlySet<string>;
  /** Every branch left in the filtered tree — shown expanded while filtering. */
  readonly openIds: ReadonlySet<string>;
  readonly matchCount: number;
}

const matches = (node: WbsNode, q: string): boolean =>
  node.label.toLowerCase().includes(q) ||
  node.code.startsWith(q) ||
  (node.hint?.toLowerCase().includes(q) ?? false);

/**
 * Keep every node that matches, all of its descendants (a matched module
 * still shows its steps), and every ancestor of a match (so the path to it
 * stays readable). A blank query returns the tree untouched.
 */
export function filterWbs(nodes: readonly WbsNode[], query: string): WbsFilterResult {
  const q = query.trim().toLowerCase();
  if (!q) return { nodes, matchIds: new Set(), openIds: new Set(), matchCount: 0 };

  const matchIds = new Set<string>();

  const prune = (list: readonly WbsNode[]): WbsNode[] =>
    list.flatMap((node) => {
      if (matches(node, q)) {
        matchIds.add(node.id);
        // Descendants that match in their own right still get highlighted.
        flatten(node.children).forEach((d) => matches(d, q) && matchIds.add(d.id));
        return [node];
      }
      const kept = prune(node.children);
      return kept.length ? [{ ...node, children: kept }] : [];
    });

  const kept = prune(nodes);
  return {
    nodes: kept,
    matchIds,
    openIds: new Set(branchIds(kept)),
    matchCount: matchIds.size,
  };
}

/** One run of a label, flagged when it is the part the query matched. */
export interface HighlightPart {
  readonly text: string;
  readonly hit: boolean;
}

/** Split a label around every case-insensitive occurrence of the query. */
export function highlightParts(text: string, query: string): HighlightPart[] {
  const q = query.trim().toLowerCase();
  if (!q) return [{ text, hit: false }];
  const parts: HighlightPart[] = [];
  const lower = text.toLowerCase();
  let at = 0;
  for (let i = lower.indexOf(q); i !== -1; i = lower.indexOf(q, at)) {
    if (i > at) parts.push({ text: text.slice(at, i), hit: false });
    parts.push({ text: text.slice(i, i + q.length), hit: true });
    at = i + q.length;
  }
  if (at < text.length) parts.push({ text: text.slice(at), hit: false });
  return parts;
}
